// Entrar con el celular y un código (documento 20, decisiones D44 y D48). Desde la decisión D51 se entra con correo y
// contraseña o con Google, Apple o Facebook: esta pantalla queda solo para las empresas que prendieron
// `acceso.celular` (se llega con "Entrar con el celular", abajo de la entrada). El código llega por SMS (en la
// demostración aparece en pantalla); con el registro abierto, un número nuevo también lo recibe y después completa los
// datos de su negocio.
import { cx, Icono } from '@reparto/ui';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { normalizarTelefono } from '../compartido';
import { Aviso, Boton } from '../componentes/base';
import { Bienvenida } from '../componentes/Bienvenida';
import { ErrorApi, mensajeDeError } from '../datos/api';
import { entrarConCodigo, pedirCodigo } from '../datos/cuenta';
import { aceptaRegistros, conCelular } from '../datos/entrada';
import { telefonoEscrito } from '../datos/formato';
import { cargarMarca, useMarca } from '../datos/marca';
import { tomarVolver } from '../datos/volver';
import { EnlacePrivacidad } from './Privacidad';

const ESPERA_REENVIO_S = 60;

function useCuentaRegresiva(hasta: number | null): number {
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    if (!hasta) return;
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [hasta]);
  return hasta ? Math.min(ESPERA_REENVIO_S, Math.max(0, Math.ceil((hasta - ahora) / 1000))) : 0;
}

export function EntrarCelular() {
  const marca = useMarca((s) => s.marca);
  const navegar = useNavigate();
  const { state } = useLocation() as { state: { paraConfirmar?: boolean } | null };
  const [paso, setPaso] = useState<'telefono' | 'codigo'>('telefono');
  const [telefono, setTelefono] = useState('');
  const [codigo, setCodigo] = useState('');
  const [demostracion, setDemostracion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [reenvioDesde, setReenvioDesde] = useState<number | null>(null);
  const faltan = useCuentaRegresiva(reenvioDesde);
  const idTel = useId();
  const idCodigo = useId();
  const campoCodigo = useRef<HTMLInputElement>(null);
  const nombre = marca?.nombre ?? 'la distribuidora';
  const nuevos = aceptaRegistros(marca?.acceso);

  useEffect(() => {
    if (paso === 'codigo') campoCodigo.current?.focus();
  }, [paso]);

  // Sin el celular prendido en esta tienda, a la entrada con correo.
  if (marca && !conCelular(marca.acceso)) return <Navigate to="/entrar" replace state={state} />;

  async function pedir(ev?: FormEvent) {
    ev?.preventDefault();
    setError(null);
    const normal = normalizarTelefono(telefono);
    if (!normal) return setError('Revisá el número. Por ejemplo: 0981 123 456');
    setOcupado(true);
    try {
      const r = await pedirCodigo(normal);
      // En la demostración se sabe si llegó un código: sin él, la tienda no acepta números nuevos.
      if (marca?.acceso.demostracion && !r.codigo_desarrollo) {
        setPaso('telefono');
        return setError(
          `El ${telefonoEscrito(telefono)} no está anotado como cliente de ${nombre}. Revisá el número o pedile a la distribuidora que lo agregue.`,
        );
      }
      setDemostracion(r.codigo_desarrollo ?? null);
      setCodigo(r.codigo_desarrollo ?? '');
      setReenvioDesde(Date.now() + ESPERA_REENVIO_S * 1000);
      setPaso('codigo');
    } catch (e) {
      if (e instanceof ErrorApi && e.codigo === 'TIENDA_CERRADA') void cargarMarca();
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  async function entrar(ev?: FormEvent, valor = codigo) {
    ev?.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(valor)) return setError('El código tiene 6 números.');
    setOcupado(true);
    try {
      const r = await entrarConCodigo(normalizarTelefono(telefono)!, valor);
      // Número nuevo: "Es tu primera vez", a completar los datos del negocio.
      if (r.estado === 'falta_completar') return navegar('/registro', { replace: true });
      const v = tomarVolver();
      navegar(v?.ruta ?? '/', { replace: true, state: v ? { confirmar: v.confirmar } : undefined });
    } catch (e) {
      // "El código no es correcto" llega sin punto ni qué hacer: se completa.
      if (e instanceof ErrorApi && e.codigo === 'OTP_INVALIDO' && !e.message.endsWith('.'))
        setError(`${e.message}. Revisalo y probá de nuevo.`);
      else setError(mensajeDeError(e));
      setOcupado(false);
    }
  }

  return (
    <Bienvenida marca={marca}>
      <Link
        to="/entrar"
        state={state}
        className="-mt-2 mb-4 -ml-2 inline-flex min-h-11 items-center gap-1 rounded-full px-2 text-[15px] font-medium text-stone-700 hover:bg-stone-200/60"
      >
        <Icono nombre="chevron_left" tamano={22} />
        Entrar con tu correo
      </Link>
      {paso === 'telefono' ? (
        <form onSubmit={pedir} noValidate>
          <h2 className="text-2xl font-bold">
            {nuevos ? 'Entrá o creá tu cuenta con tu celular' : 'Entrá con tu celular'}
          </h2>
          <p className="mt-1 text-stone-600">Te mandamos un código para entrar, sin contraseña.</p>
          <label htmlFor={idTel} className="mt-6 block text-base font-semibold text-stone-800">
            Número de celular
          </label>
          <div className="relative mt-2">
            <Icono
              nombre="smartphone"
              className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-stone-600"
            />
            <input
              id={idTel}
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              placeholder="0981 123 456"
              value={telefono}
              onChange={(e) => setTelefono(telefonoEscrito(e.target.value))}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${idTel}-error` : undefined}
              className={cx(
                'h-15 w-full rounded-2xl border-2 bg-white pr-4 pl-13 text-[22px] tracking-wide tabular-nums placeholder:text-stone-500',
                'focus:border-primary-600 focus:ring-4 focus:ring-primary-200 focus:outline-none',
                error ? 'border-rose-500' : 'border-stone-300',
              )}
            />
          </div>
          {error ? (
            <p id={`${idTel}-error`} role="alert" className="mt-2 text-[15px] font-medium text-rose-700">
              {error}
            </p>
          ) : null}
          <Boton type="submit" bloque tamano="lg" icono="sms" cargando={ocupado} className="mt-6 text-lg!">
            Recibir código
          </Boton>
        </form>
      ) : (
        <form onSubmit={entrar} noValidate>
          <h2 className="text-2xl font-bold">Escribí el código</h2>
          <p className="mt-1 text-stone-600">
            {demostracion ? 'Es el código para entrar con el ' : 'Te lo mandamos por SMS al '}
            <b className="whitespace-nowrap text-stone-900 tabular-nums">{telefono}</b>.
          </p>
          {demostracion ? (
            <Aviso tono="aviso" icono="info" className="mt-4" titulo={`Código de demostración: ${demostracion}`}>
              Esta tienda es de prueba, por eso el código aparece acá. Con la tienda real llega por SMS.
            </Aviso>
          ) : null}
          <label htmlFor={idCodigo} className="mt-6 block text-base font-semibold text-stone-800">
            Código de 6 números
          </label>
          <input
            ref={campoCodigo}
            id={idCodigo}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            value={codigo}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 6);
              setCodigo(v);
              // Con los 6 números (escritos o pegados del SMS) entra solo.
              if (v.length === 6 && !ocupado) void entrar(undefined, v);
            }}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${idCodigo}-error` : undefined}
            className={cx(
              'mt-2 h-16 w-full rounded-2xl border-2 bg-white text-center text-[32px] font-bold tracking-[0.45em] tabular-nums',
              'focus:border-primary-600 focus:ring-4 focus:ring-primary-200 focus:outline-none',
              error ? 'border-rose-500' : 'border-stone-300',
            )}
          />
          {error ? (
            <p id={`${idCodigo}-error`} role="alert" className="mt-2 text-[15px] font-medium text-rose-700">
              {error}
            </p>
          ) : null}
          <Boton type="submit" bloque tamano="lg" icono="login" cargando={ocupado} className="mt-6 text-lg!">
            Entrar
          </Boton>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Boton
              variante="secundario"
              disabled={faltan > 0 || ocupado}
              onClick={() => void pedir()}
              className="h-auto! min-h-12 px-3! py-2 leading-tight"
            >
              {faltan > 0 ? `Otro código en ${faltan} s` : 'Pedir otro código'}
            </Boton>
            <Boton
              variante="secundario"
              onClick={() => {
                setPaso('telefono');
                setCodigo('');
                setError(null);
                setDemostracion(null);
              }}
              className="h-auto! min-h-12 px-3! py-2 leading-tight"
            >
              Cambiar número
            </Boton>
          </div>
          {nuevos ? (
            <p className="mt-6 flex items-start gap-2 text-[15px] text-stone-600">
              <Icono nombre="storefront" tamano={20} className="mt-px shrink-0 text-stone-600" />
              ¿Es la primera vez que pedís a {nombre}? Después del código completás los datos de tu negocio.
            </p>
          ) : null}
        </form>
      )}
      <p className="mt-8 text-center">
        <EnlacePrivacidad />
      </p>
    </Bienvenida>
  );
}
